import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Tag, ArrowLeft, Calendar, Share2, CheckCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import NewsletterSignup from '../components/NewsletterSignup';
import api from '../utils/api';

function renderMarkdown(md) {
  if (!md) return '';
  let html = md
    .replace(/### (.+)/g, '<h3 class="font-serif text-xl md:text-2xl text-foreground mt-10 mb-4">$1</h3>')
    .replace(/## (.+)/g, '<h2 class="font-serif text-2xl md:text-3xl text-foreground mt-12 mb-5">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)/gm, '<li class="ml-4 mb-2 text-muted-foreground leading-relaxed list-disc">$1</li>')
    .replace(/^\d+\. (.+)/gm, '<li class="ml-4 mb-2 text-muted-foreground leading-relaxed list-decimal">$1</li>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\n\n/g, '</p><p class="text-muted-foreground leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');
  return `<p class="text-muted-foreground leading-relaxed mb-4">${html}</p>`;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/blog/post/${slug}`);
        setPost(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const sharePost = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white/5 rounded w-3/4 mx-auto" />
            <div className="h-4 bg-white/5 rounded w-1/2 mx-auto" />
            <div className="h-64 bg-white/5 rounded mt-8" />
          </div>
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <h1 className="font-serif text-3xl mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">This blog post doesn't exist or has been removed.</p>
          <Link to="/blog" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SeoHead
        title={`${post.title} — Writer's Blogck | AI Ghostwriter Pro`}
        description={post.meta_description}
        keywords={post.tags ? post.tags.join(', ') + ', ghostwriting, book writing, hire a ghostwriter' : 'ghostwriting, book writing tips'}
        schemas={post.article_schema ? [post.article_schema] : []}
        image={post.hero_image_url}
        imageAlt={`${post.title} — professional ghostwriting and book publishing insights for aspiring authors`}
        path={`/blog/${post.slug}`}
        article={{ published_at: post.published_at, tags: post.tags }}
      />

      <article className="pt-32 pb-24 md:pt-40 md:pb-32" data-testid="blog-post-article">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav className="mb-8" data-testid="blog-breadcrumb">
            <Link to="/blog" className="text-xs uppercase tracking-[0.2em] text-primary hover:text-foreground transition-colors inline-flex items-center gap-1.5">
              <ArrowLeft className="w-3 h-3" /> Writer's Blogck
            </Link>
          </nav>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="post-tags">
              {post.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag}`}
                  className="px-3 py-1 rounded-sm text-[10px] uppercase tracking-widest font-bold bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6" data-testid="post-title">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-white/5" data-testid="post-meta">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {formatDate(post.published_at)}
            </span>
            {post.estimated_read_time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {post.estimated_read_time}
              </span>
            )}
            <button onClick={sharePost} className="flex items-center gap-1.5 hover:text-primary transition-colors ml-auto" data-testid="share-post">
              {copied ? <><CheckCircle className="w-4 h-4 text-green-400" /> Copied!</> : <><Share2 className="w-4 h-4" /> Share</>}
            </button>
          </div>

          {/* Hero Image */}
          <div className="h-64 md:h-80 rounded-sm mb-10 relative overflow-hidden" data-testid="post-hero-image">
            {post.hero_image_url ? (
              <img
                src={post.hero_image_url}
                alt={post.hero_image_alt || post.image_alt_1 || `${post.title} — ghostwriting and book publishing guide for aspiring authors`}
                className="w-full h-full object-cover opacity-80"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/10 via-background to-primary/5 flex items-center justify-center">
                <span className="font-serif text-3xl text-primary/20 select-none">Writer's Blogck</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div
            className="prose prose-invert max-w-none blog-content"
            data-testid="post-content"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />

          {/* Keywords for SEO (hidden) */}
          {post.all_keywords && (
            <meta name="keywords" content={post.all_keywords.join(', ')} />
          )}

          {/* Newsletter */}
          <div className="mt-12 mb-8" data-testid="post-newsletter">
            <NewsletterSignup source={`blog-post-${slug}`} />
          </div>

          {/* CTA */}
          <div className="mt-16 py-12 px-8 bg-white/5 border border-white/10 rounded-sm text-center" data-testid="post-cta">
            <h3 className="font-serif text-2xl mb-3">Ready to Write Your Book?</h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Stop dreaming about your book and start creating it. AI Ghostwriter Pro turns your ideas into publication-ready manuscripts.
            </p>
            <Link
              to="/pricing"
              data-testid="post-cta-link"
              className="inline-flex items-center gap-2 rounded-sm px-8 py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]"
            >
              View Plans — Starting at $149/mo
            </Link>
          </div>

          {/* Back to Blog */}
          <div className="mt-12 text-center">
            <Link to="/blog" className="text-primary hover:underline inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-widest" data-testid="back-to-blog-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Writer's Blogck
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
